import React, { useEffect, useRef, useState, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

// Components
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import BrandThesis from './components/BrandThesis';
import EarlyAccessCTA from './components/EarlyAccessCTA';
import EmailCapture from './components/EmailCapture';
import ZurianStandard from './components/ZurianStandard';
import CrossFadeSection from './components/CrossFadeSection';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';

// Pages
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Banner
import { AnnouncementBannerSlot } from './components/announcement-banner-slot';

function modeForPath(pathname) {
  const p = pathname.replace(/\/$/, "").toLowerCase();
  if (p === "" || p === "/") return "home";
  if (p === "/privacy-policy" || p === "/terms-of-service") return "hidden";
  return "standard";
}

const Home = ({ isLoading }) => {
  const ctaRef = useRef(null);
  const location = useLocation();
  const isInterruptedRef = useRef(false);
  const ctaScrollRafRef = useRef(null);

  // Robust requestAnimationFrame smooth scroll that cannot be interrupted by top banner collapse,
  // ResizeObservers, or layout shifts
  const smoothScrollToCTA = (targetY, duration = 850) => {
    if (ctaScrollRafRef.current) {
      cancelAnimationFrame(ctaScrollRafRef.current);
      ctaScrollRafRef.current = null;
    }

    const startY = window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 5) return;

    let startTime = null;
    let userCancelled = false;

    // Detect if user deliberately scrolls/touches to take over
    const onUserInterrupt = () => {
      userCancelled = true;
      cleanup();
    };

    const addListeners = () => {
      window.addEventListener('wheel', onUserInterrupt, { passive: true });
      window.addEventListener('touchmove', onUserInterrupt, { passive: true });
    };

    const cleanup = () => {
      window.removeEventListener('wheel', onUserInterrupt);
      window.removeEventListener('touchmove', onUserInterrupt);
      ctaScrollRafRef.current = null;
    };

    // Smooth cubic ease-in-out
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const step = (now) => {
      if (userCancelled) return;
      if (!startTime) startTime = now;
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = easeInOutCubic(progress);

      window.scrollTo(0, Math.round(startY + distance * ease));

      if (progress < 1) {
        ctaScrollRafRef.current = requestAnimationFrame(step);
      } else {
        window.scrollTo(0, targetY);
        cleanup();
      }
    };

    addListeners();
    ctaScrollRafRef.current = requestAnimationFrame(step);
  };

  const scrollToCTA = useCallback(() => {
    // Immediately cancel any in-flight cinematic auto-scroll so the user reaches the email section accurately
    isInterruptedRef.current = true;

    const element = ctaRef.current || document.getElementById('email-capture');
    if (element) {
      // Calculate dynamic top offset for fixed header at destination (top banner is hidden when scrolled)
      const headerEl = document.querySelector('header');
      const headerHeight = headerEl ? headerEl.offsetHeight : 64;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const targetY = Math.max(0, elementTop - headerHeight - 20);

      smoothScrollToCTA(targetY, 850);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (ctaScrollRafRef.current) {
        cancelAnimationFrame(ctaScrollRafRef.current);
      }
    };
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Check for scroll state from other pages
  useEffect(() => {
    if (location.state?.scrollToEmail && !isLoading) {
      isInterruptedRef.current = true;
      const timer = setTimeout(() => {
        scrollToCTA();
        window.history.replaceState({}, document.title);
      }, 150); // Delay ensures DOM layout is ready
      return () => clearTimeout(timer);
    }
  }, [location, isLoading, scrollToCTA]);

  // Track scroll position for dynamic scrollbar
  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll percentage (0 to 100)
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0;
      document.documentElement.style.setProperty('--scroll-pos', `${scrollPercent}%`);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initialize on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cinematic Orchestration: Glide UP then Glide DOWN
  useEffect(() => {
    // Only start the sequence AFTER the loading screen is gone
    if (isLoading) return;

    // Session Intelligence: Check if user returned within 5 minutes
    const now = Date.now();
    const lastSessionTime = sessionStorage.getItem("teaser_last_scroll_time");
    const isQuickReturn = lastSessionTime && (now - parseInt(lastSessionTime) < 5 * 60 * 1000);
    
    // Auto-scroll down duration unified to exactly 2.0 seconds (2000ms) for consistent speed
    const scrollDuration = 2000;

    // Keep auto restoration so we know where we landed after refresh
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "auto";
    }

    // Detect user interaction to cancel the sequence immediately
    const interrupt = () => { isInterruptedRef.current = true; };
    const addListeners = () => {
      window.addEventListener('wheel', interrupt, { passive: true });
      window.addEventListener('touchmove', interrupt, { passive: true });
      window.addEventListener('touchstart', interrupt, { passive: true });
      window.addEventListener('mousedown', interrupt, { passive: true });
      window.addEventListener('keydown', interrupt, { passive: true });
    };
    const removeListeners = () => {
      window.removeEventListener('wheel', interrupt);
      window.removeEventListener('touchmove', interrupt);
      window.removeEventListener('touchstart', interrupt);
      window.removeEventListener('mousedown', interrupt);
      window.removeEventListener('keydown', interrupt);
    };

    addListeners();

    const animateScroll = (targetY, duration) => {
      return new Promise((resolve) => {
        const startPos = window.scrollY;
        const distance = targetY - startPos;
        let startTime = null;

        function step(currentTime) {
          if (isInterruptedRef.current) {
            resolve();
            return;
          }

          if (!startTime) startTime = currentTime;
          const timeElapsed = currentTime - startTime;
          const progress = Math.min(timeElapsed / duration, 1);

          // Linear motion
          const ease = progress;

          window.scrollTo(0, startPos + distance * ease);

          if (timeElapsed < duration) {
            requestAnimationFrame(step);
          } else {
            resolve();
          }
        }
        requestAnimationFrame(step);
      });
    };

    const runCinematicSequence = async () => {
      if (isInterruptedRef.current || location.state?.scrollToEmail) return;

      const currentScroll = window.scrollY;

      // Glide UP to the top if we started down the page
      if (currentScroll > 20) {
        const upVelocity = isQuickReturn ? 6.0 : 3.5;
        const upDuration = Math.min(Math.max(currentScroll / upVelocity, 300), 1800);
        await animateScroll(0, upDuration);
      }

      if (isInterruptedRef.current || location.state?.scrollToEmail) return;

      // Signaling for potential staggered reveals
      document.documentElement.setAttribute('data-scroll-reveal', 'true');

      // Glide DOWN to target animations (unified 2.0s duration)
      const targetPos = 800;
      await animateScroll(targetPos, scrollDuration);

      // Mark completion
      sessionStorage.setItem("teaser_last_scroll_time", Date.now().toString());
    };

    runCinematicSequence();

    return () => {
      isInterruptedRef.current = true;
      removeListeners();
    };
  }, [isLoading, location.state]);

  return (
    <div className="min-h-screen bg-primary-navy">

      <Header onInnerCircleClick={scrollToCTA} />
      <HeroSection />
      <CrossFadeSection />
      <EmailCapture ref={ctaRef} />
      <ZurianStandard />
      <Footer />
    </div>
  );
};

function AppContent() {
  const location = useLocation();

  const [loading, setLoading] = useState(() => {
    const path = window.location.pathname.replace(/\/$/, "").toLowerCase();
    const isBypassRoute = path === "/privacy-policy" || path === "/terms-of-service";
    if (isBypassRoute) return false;
    return sessionStorage.getItem('hasSeenLoadingScreen') !== 'true';
  });
  const [showContent, setShowContent] = useState(() => {
    const path = window.location.pathname.replace(/\/$/, "").toLowerCase();
    const isBypassRoute = path === "/privacy-policy" || path === "/terms-of-service";
    if (isBypassRoute) return true;
    return sessionStorage.getItem('hasSeenLoadingScreen') === 'true';
  });

  useEffect(() => {
    if (loading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [loading]);

  useEffect(() => {
    // Start mounting the main content shortly after the loading screen 
    // mounts to fetch background assets, while leaving the main thread
    // free for the first initialization frames of the Lottie animation.
    if (loading) {
      const contentTimer = setTimeout(() => {
        setShowContent(true);
      }, 400); // Wait 400ms for smooth first-frames

      return () => clearTimeout(contentTimer);
    }
  }, [loading]);

  // Route listener: Trigger loading screen when navigating client-side to the home page if not seen yet
  useEffect(() => {
    const path = location.pathname.replace(/\/$/, "").toLowerCase();
    const isHomeRoute = path === "" || path === "/";
    if (isHomeRoute && sessionStorage.getItem('hasSeenLoadingScreen') !== 'true' && !loading) {
      setLoading(true);
      setShowContent(false);
    }
  }, [location.pathname, loading]);

  const handleLoadingComplete = () => {
    setLoading(false);
    sessionStorage.setItem('hasSeenLoadingScreen', 'true');
  };

  return (
    <div className="App bg-primary-navy">
      <AnnouncementBannerSlot mode={modeForPath(location.pathname)} />

      {loading && (
        <LoadingScreen
          onComplete={handleLoadingComplete}
        />
      )}

      {/* Heavy routes render behind the fixed Z-[100] LoadingScreen, pre-loading background images naturally */}
      {showContent && (
        <Routes>
          <Route path="/" element={<Home isLoading={loading} />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
        </Routes>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
